import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, Observable } from 'rxjs';
import { switchMap, take, filter, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly firestoreService = inject(FirestoreService);

  readonly SUPPORTED_LANGS = ['es', 'en'];
  readonly DEFAULT_LANG = 'es';

  private langInitialized = new BehaviorSubject<boolean>(false);
  langInitialized$ = this.langInitialized.asObservable().pipe(filter(initialized => initialized));

  constructor() {
    this.translate.addLangs(this.SUPPORTED_LANGS);
    this.translate.setDefaultLang(this.DEFAULT_LANG);
    this.translate.use(this.DEFAULT_LANG);
  }

  /**
   * Initializes the language service, determining the initial language based on a priority order:
   * 1. Logged-in user's preference from Firestore.
   * 2. Language preference from localStorage.
   * 3. Browser's language.
   * 4. Fallback to default language ('es').
   */
  init(): void {
    this.authService.currentUser$.pipe(
      take(1), // We only need the initial user state to decide the language path
      switchMap(user => {
        if (user) {
          // User is logged in, try Firestore first
          return this.firestoreService.getUserProfile(user.uid).pipe(
            switchMap(profile => {
              const firestoreLang = profile?.lang;
              if (firestoreLang && this.SUPPORTED_LANGS.includes(firestoreLang)) {
                return of(firestoreLang);
              }
              // If no lang in firestore, fall through to localStorage/browser
              return this.getLocalStorageOrBrowserLang();
            })
          );
        } else {
          // No user, check localStorage and then browser
          return this.getLocalStorageOrBrowserLang();
        }
      })
    ).subscribe(lang => {
      this.translate.use(lang);
      this.langInitialized.next(true);
    });

    // Listen for real-time language changes from Firestore for logged-in users
    this.authService.currentUser$.pipe(
      switchMap(user => user ? this.firestoreService.getUserProfile(user.uid) : of(null)),
      filter(profile => !!profile && !!profile.lang && this.SUPPORTED_LANGS.includes(profile.lang)),
      distinctUntilChanged((prev, curr) => prev?.lang === curr?.lang)
    ).subscribe(profile => {
      if (profile && this.translate.currentLang !== profile.lang) {
        this.translate.use(profile.lang as string);
      }
    });
  }

  private getLocalStorageOrBrowserLang(): Observable<string> {
    const localLang = localStorage.getItem('lang');
    if (localLang && this.SUPPORTED_LANGS.includes(localLang)) {
      return of(localLang);
    }

    const browserLang = this.translate.getBrowserLang();
    if (browserLang && this.SUPPORTED_LANGS.includes(browserLang)) {
      return of(browserLang);
    }

    return of(this.DEFAULT_LANG);
  }

  /**
   * Sets the application language and persists the preference.
   * @param lang The language code (e.g., 'es', 'en').
   */
  async setUserLanguage(lang: string): Promise<void> {
    if (!this.SUPPORTED_LANGS.includes(lang)) {
      console.warn(`[LanguageService] Attempted to set unsupported language: ${lang}`);
      return;
    }

    this.translate.use(lang);
    localStorage.setItem('lang', lang);

    const user = await this.authService.currentUser$.pipe(take(1)).toPromise();
    if (user) {
      try {
        await this.firestoreService.updateUserProfile(user.uid, { lang });
      } catch (error) {
        console.error('[LanguageService] Failed to save language to Firestore', error);
      }
    }
  }

  /**
   * Gets the currently active language.
   */
  get currentLang(): string {
    return this.translate.currentLang || this.DEFAULT_LANG;
  }
}
